/**
 * S2 · Selfie upload.
 * Uploads a raw selfie via `generate-avatar action=get-upload-url` + signed PUT.
 * DO NOT call `attach_selfie_to_guest_session` here — B4 refuses without consent (Group 2).
 * We only persist the path locally; S3 will bind it after consent + attach.
 *
 * LANE 2 (repeat buyer, already authenticated): email + OTP are skipped, so S3
 * is not on the path. A retake is still a FRESH biometric capture, so this
 * screen captures fresh user-scope consent inline and binds it before handing
 * off to generating. The server gate is unchanged — `attach_selfie_to_user`
 * refuses without a granted user-scope biometric consent row.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getUploadUrl, uploadSelfie, attachSelfieToUser, getSelfieSignedUrl } from "@/services/avatar";
import { saveSelfiePath, readSelfiePath, readStylePick, recordFunnelEvent } from "@/lib/consumer-session";

import { useRepeatProfile } from "@/lib/repeat-flow";
import { useConsumerFlow } from "../ConsumerFlow";
import { findTile, tileColor } from "@/lib/style-art";
import {
  FunnelPage,
  FunnelContainer,
  FunnelHeader,
  Display,
  Lede,
  SectionTitle,
  Panel,
  StickyBar,
  PrimaryButton,
  GhostButton,
  SentenceLink,
  ShieldIcon,
  GoodRow,
  BadRow,
  CardFace,
} from "@/components/consumer/chrome";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S2_SECTION, S2_CONTENT, S2_DEFAULTS } from "@/content/screens/s2";

export default function S2Selfie() {
  const { program } = useConsumerFlow();
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selfiePath, setSelfiePath] = useState<string | null>(() => readSelfiePath(program.slug));
  const isMobile = useIsMobile();
  const [preview, setPreview] = useState<string | null>(null);

  /**
   * ONE derived CTA state, from whether a selfie is present — never from HOW it
   * arrived (mobile camera and laptop file-load both land in `selfiePath`).
   *
   *   "empty"    → no image at all: capture is the single primary, Continue disabled.
   *   "pending"  → an image is picked and uploading: capture is already
   *                de-emphasised (it must not stay bold while the picked image is
   *                on screen — that was the reported bug), Continue shows progress.
   *   "ready"    → uploaded: Continue is the ONLY primary, capture becomes a
   *                quiet "Retake" ghost.
   */
  const ctaState: "empty" | "pending" | "ready" = uploading
    ? "pending"
    : selfiePath
      ? "ready"
      : "empty";
  const hasSelfie = ctaState === "ready";
  const repeat = useRepeatProfile(program.slug);
  const [reconsent, setReconsent] = useState(false);
  const [binding, setBinding] = useState(false);
  const { t } = useSiteContent(S2_SECTION, S2_DEFAULTS);



  const stylePick = readStylePick(program.slug);
  const styleMeta = findTile(program.styles, stylePick?.style_id);
  const styleName = styleMeta?.name || stylePick?.style_id || t("s2.style.fallbackName");
  const styleColorValue = tileColor(program.styles, stylePick?.style_id);

  /**
   * IDENTIFIED continue (Lane 2, and any already-verified session that came
   * back to change something): record a FRESH user-scope biometric consent for
   * this new capture, bind the selfie, then go straight to generating — no OTP
   * screen, because the session is already verified.
   */
  const continueRepeat = async () => {
    if (!selfiePath) return;
    setBinding(true);
    setError(null);
    try {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth?.user) throw new Error(t("s2.errors.notSignedIn"));
      const { data: version } = await supabase.rpc("current_consent_policy_version", {
        p_consent_type: "biometric",
      });
      const policyVersion = Number(version ?? 1);
      const { error: consentErr } = await supabase.rpc("record_consent", {
        p_scope_type: "user",
        p_scope_id: auth.user.id,
        p_consent_type: "biometric",
        p_policy_version: policyVersion,
        p_action: "granted",
        p_meta: { source: "repeat_retake", selfie_path: selfiePath } as any,
      });
      if (consentErr) throw consentErr;
      const attach = await attachSelfieToUser(selfiePath, policyVersion);
      if ((attach as any).error) throw (attach as any).error;
      navigate("../generating");
    } catch (e: any) {
      setError(e?.message || t("s2.errors.consentFailed"));
    } finally {
      setBinding(false);
    }
  };


  /** Returning user: a stored path already means "ready", so show that selfie. */
  useEffect(() => {
    let cancelled = false;
    if (!selfiePath || preview) return;
    getSelfieSignedUrl(selfiePath).then(({ data }) => {
      if (!cancelled && data?.signedUrl) setPreview(data.signedUrl);
    });
    return () => { cancelled = true; };
  }, [selfiePath, preview]);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    // Second half of the gate on the client: even a synthesised change event on
    // the file input cannot start an upload without the affirmation.

    setError(null);
    // Reflect the pick INSTANTLY: the dropzone shows the picked image and the
    // CTA drops out of the primary state before the network round-trip lands.
    const localPreview = URL.createObjectURL(file);
    setPreview(localPreview);
    setUploading(true);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 6) || "jpg";
      const { data, error: fnErr } = await getUploadUrl(ext);
      if (fnErr || !data?.signedUrl || !data?.path) {
        throw new Error(fnErr?.message || t("s2.errors.uploadUrlFailed"));
      }
      await uploadSelfie(data.signedUrl, file);
      saveSelfiePath(program.slug, data.path);
      recordFunnelEvent(program.slug, "selfie_uploaded", program.program_id);
      setSelfiePath(data.path);
    } catch (e: any) {
      // Upload failed => there is NO selfie. Roll the preview back so the screen
      // cannot sit in a "looks loaded, CTA bold" half-state; capture returns as
      // the primary action next to the error.
      setPreview(null);
      URL.revokeObjectURL(localPreview);
      setSelfiePath(null);
      setError(e?.message || t("s2.errors.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };


  return (

    <FunnelPage testId="s2-selfie">
      <FunnelContainer size="form">
        <FunnelHeader backTo="../style" backLabel={t("s2.header.backLabel")} step={t("s2.header.step")} />

        {/*
          B4 · the chosen style's ARTWORK, not just its colour. This tile rendered
          as a bare coloured rectangle — the card looked blank the moment a style
          was picked — because `art` was never passed.
          U1 · no "Change" link beside it; the header back arrow is the one way back.
        */}
        <div className="mb-5 flex items-center gap-3 rounded-[14px] border border-foreground/10 bg-white p-[10px] pr-[14px]">
          <CardFace
            color={styleColorValue}
            art={styleMeta?.art ?? null}
            alt={styleName}
            className="w-[38px] flex-none rounded-[6px]"
          />
          <div className="flex min-w-0 flex-col gap-px">
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-muted">{t("s2.style.label")}</span>
            <span className="text-[15px] font-semibold tracking-[-0.01em]">{styleName}</span>
          </div>
        </div>

        <Display className="mb-3 max-w-[18ch]">{t("s2.header.title")}</Display>
        <Lede className="mb-6 max-w-[44ch]">
          {t("s2.header.subtitle")}
        </Lede>


        <label
          htmlFor="s2-file"
          className={
            "relative mx-auto mb-7 grid aspect-[4/5] w-full max-w-[300px] cursor-pointer place-items-center overflow-hidden rounded-[20px] border border-foreground/10 bg-white"
          }
          data-testid="s2-selfie-dropzone"
        >
          {preview ? (

            <img src={preview} alt="Preview" className="absolute inset-0 h-full w-full object-cover" data-testid="s2-selfie-preview" />
          ) : (
            <>
              <svg viewBox="0 0 240 300" className="absolute inset-0 h-full w-full">
                <ellipse cx="120" cy="140" rx="72" ry="92" fill="none" stroke="#8834B2" strokeWidth="2.5" strokeDasharray="10 9" opacity="0.5" />
                <path d="M22 22h30M22 22v30" stroke="#1C2030" strokeWidth="3" strokeLinecap="round" opacity="0.25" fill="none" />
                <path d="M218 22h-30M218 22v30" stroke="#1C2030" strokeWidth="3" strokeLinecap="round" opacity="0.25" fill="none" />
                <path d="M22 278h30M22 278v-30" stroke="#1C2030" strokeWidth="3" strokeLinecap="round" opacity="0.25" fill="none" />
                <path d="M218 278h-30M218 278v-30" stroke="#1C2030" strokeWidth="3" strokeLinecap="round" opacity="0.25" fill="none" />
              </svg>
              <span className="relative px-8 text-center text-[14px] font-medium leading-[1.4] text-slate-muted whitespace-pre-line">
                {t("s2.dropzone.hint")}
              </span>
            </>
          )}
        </label>
        <input
          id="s2-file"
          type="file"
          accept="image/*"
          capture="user"
          className="sr-only"
          data-testid="s2-selfie-file-input"
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        {/*
          F2 · a second, capture-free input. The camera-first input is the right
          one on a phone; on a laptop "Upload a photo" must open the file picker,
          which `capture` interferes with on some browsers.
        */}
        <input
          id="s2-file-upload"
          type="file"
          accept="image/*"
          className="sr-only"
          data-testid="s2-selfie-upload-input"
          onChange={(e) => onFile(e.target.files?.[0])}
        />


        <SectionTitle className="mb-3">{t("s2.good.title")}</SectionTitle>
        <div className="mb-[30px] flex flex-col gap-2">
          <GoodRow>{t("s2.good.light")}</GoodRow>
          <GoodRow>{t("s2.good.alone")}</GoodRow>
          <GoodRow>{t("s2.good.straight")}</GoodRow>
          <GoodRow>{t("s2.good.smile")}</GoodRow>
        </div>

        <SectionTitle className="mb-1.5">{t("s2.bad.title")}</SectionTitle>
        <p className="mb-3 text-[14px] leading-[1.45] text-foreground/60">{t("s2.bad.intro")}</p>
        <div className="mb-[26px] flex flex-col gap-2 rounded-[18px] bg-[#EC1314]/[0.05] p-1">
          <BadRow>{t("s2.bad.sunglasses")}</BadRow>
          <BadRow>{t("s2.bad.filters")}</BadRow>
          <BadRow>{t("s2.bad.otherFace")}</BadRow>
          <BadRow>{t("s2.bad.logos")}</BadRow>
          <BadRow>{t("s2.bad.blurry")}</BadRow>
        </div>

        <Panel className="mb-4 flex items-start gap-3">
          <ShieldIcon className="mt-px" />
          <span className="text-[14px] leading-[1.5] text-foreground/70">
            {t("s2.privacy.body")} <a href="#">{t("s2.privacy.link")}</a>
          </span>
        </Panel>

        {/* Lane 2 — a retake is a new capture, so consent is asked again here
            (there is no OTP screen on the repeat path to ask it on). */}
        {repeat.isRepeat && selfiePath && !uploading ? (
          <label
            className="mb-4 flex cursor-pointer items-start gap-3 rounded-[18px] border border-foreground/[0.16] bg-white p-4"
            data-testid="s2-selfie-reconsent"
          >
            <input
              type="checkbox"
              checked={reconsent}
              onChange={(e) => setReconsent(e.target.checked)}
              className="mt-0.5 h-[18px] w-[18px] flex-none accent-[#8834B2]"
              data-testid="s2-selfie-reconsent-input"
            />
            <span className="text-[14px] leading-[1.5] text-foreground/75">
              {t("s2.reconsent.body")}
            </span>
          </label>
        ) : null}

        {uploading && <p className="mt-4 text-foreground/60" data-testid="s2-selfie-uploading">{t("s2.body.uploading")}</p>}
        {error && <p className="mt-4 text-[#EC1314]" data-testid="s2-selfie-error">{error}</p>}
        {selfiePath && !uploading && (
          <p className="mt-4 break-all text-[13px] text-foreground/55" data-testid="s2-selfie-path">
            {repeat.isRepeat
              ? t("s2.body.readyRepeat")
              : t("s2.body.readyNew")}
          </p>
        )}
      </FunnelContainer>

      {/* CTA state is derived from "is there a selfie?", not from HOW it arrived —
          camera capture on mobile and file-load on desktop both land in selfiePath.
          No selfie: capture is the single primary, Continue is disabled.
          Selfie loaded: capture de-emphasises to a secondary "Retake", the
          upload-instead ghost disappears (retake covers both paths), and
          Continue is the only primary. */}
      <StickyBar data-cta-state={ctaState}>
        {ctaState === "pending" ? (
          <>
            <PrimaryButton disabled data-testid="s2-selfie-continue" className="order-1 basis-full">
              {t("s2.body.uploading")}
            </PrimaryButton>
            <GhostButton disabled data-testid="s2-selfie-retake" className="order-2 basis-full">
              {t("s2.actions.retake")}
            </GhostButton>
          </>
        ) : hasSelfie ? (
          <>
            <PrimaryButton
              disabled={uploading || binding || (repeat.isRepeat && !reconsent)}
              /* Identity gate: a signed-in person is already verified, so a new
                 capture binds consent + selfie to the USER and goes straight to
                 generating. Only an anonymous visitor goes to S3 (email + OTP). */
              onClick={() => (repeat.isIdentified ? continueRepeat() : navigate("../verify"))}
              data-testid="s2-selfie-continue"
              className="order-1 basis-full"
            >
              {binding ? t("s2.actions.recordingConsent") : t("s2.actions.continue")}
            </PrimaryButton>
            <GhostButton
              onClick={() => document.getElementById("s2-file")?.click()}
              disabled={uploading || binding}
              data-testid="s2-selfie-retake"
              className="order-2 basis-full"
            >
              {t("s2.actions.retake")}
            </GhostButton>
          </>
        ) : (
          <>
            {/*
              F2/G3 · the primary follows the DEVICE — camera on a phone, file
              picker on a laptop — and the other route is a sentence link, not a
              competing button.
            */}
            <PrimaryButton
              onClick={() => document.getElementById(isMobile ? "s2-file" : "s2-file-upload")?.click()}
              disabled={uploading}
              data-testid="s2-selfie-capture"
              className="order-1 flex items-center justify-center gap-2.5 basis-full"
            >
              <svg viewBox="0 0 22 22" className="h-5 w-5">
                <path d="M2.6 7.4h3.1l1.5-2.4h7.6l1.5 2.4h3.1v10.2H2.6z" fill="none" stroke="#FFFFFF" strokeWidth="1.9" strokeLinejoin="round" />
                <circle cx="11" cy="12.3" r="3.4" fill="none" stroke="#FFFFFF" strokeWidth="1.9" />
              </svg>
              {isMobile ? t("s2.actions.capture") : t("s2.actions.upload")}
            </PrimaryButton>
            <SentenceLink
              onClick={() => document.getElementById(isMobile ? "s2-file-upload" : "s2-file")?.click()}
              disabled={uploading}
              data-testid="s2-selfie-upload-alt"
              className="order-2 basis-full text-center"
            >
              {isMobile ? t("s2.actions.uploadAlt") : t("s2.actions.captureAlt")}
            </SentenceLink>
          </>

        )}
      </StickyBar>

    </FunnelPage>
  );
}
